import React, { useState, useEffect, useRef, useCallback, useLayoutEffect } from "react";
import { Routes, Route, useNavigate, Link, Navigate } from "react-router-dom";
import AdminLayout from "../components/admin/AdminLayout";
import DashboardOverview from "./DashboardOverview";
import BookingsManagement from "./BookingsManagement";
import Analytics from "./Analytics";
import FleetManagement from "./FleetManagement";
import Settings from "./Settings";
import UserManagement from "./admin/UserManagement";
import { useMaps } from "../components/MapsProvider";

// Custom error boundary for AdminDashboard
class AdminErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error) {
    // Return null to prevent state update which triggers re-render
    return null;
  }

  componentDidCatch(error, errorInfo) {
    console.log("AdminDashboard error suppressed:", error.message);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="flex flex-col items-center justify-center h-screen bg-black text-white">
          <h2 className="text-xl font-bold text-white mb-3">Dashboard Error</h2>
          <p className="mb-4">Something went wrong loading the admin dashboard.</p>
          <button 
            onClick={() => window.location.reload()}
            className="px-4 py-2 bg-gradient-to-r from-white to-yellow-600 text-black rounded-md"
          >
            Reload Dashboard
          </button>
        </div>
      );
    }

    return this.props.children;
  }
}

// Helper to render a protected component with maps
const renderMapComponent = (Component) => {
  return (
    <AdminErrorBoundary>
      <Component />
    </AdminErrorBoundary>
  );
};

const AdminDashboard = () => {
  const navigate = useNavigate();
  const { forceLoaded } = useMaps();
  const [mapsInitialized, setMapsInitialized] = useState(false);
  
  // Use layout effect for earlier execution before render
  useLayoutEffect(() => {
    // Disable React error overlay immediately
    if (window.__REACT_ERROR_OVERLAY__) {
      window.__REACT_ERROR_OVERLAY__.handleRuntimeError = function() {};
      window.__REACT_ERROR_OVERLAY__.startReportingRuntimeErrors = function() {};
      window.__REACT_ERROR_OVERLAY__.dismissRuntimeErrors = function() {};
    }
    
    // Suppress script errors
    const handleError = (e) => {
      if (e.message === 'Script error.' || !e.filename) {
        e.stopPropagation();
        e.preventDefault();
        return true;
      }
    };
    
    window.addEventListener('error', handleError, true);
    
    return () => {
      window.removeEventListener('error', handleError, true);
    };
  }, []);
  
  // Auto-redirect to overview on first load and force maps to load
  useEffect(() => {
    // Check if we're already forcing maps to load
    if (window.adminMapsLoadingInitiated) {
      return;
    }
    
    // Set flag to prevent duplicate initialization
    window.adminMapsLoadingInitiated = true;
    console.log("AdminDashboard - initializing maps with delay");
    
    // Delay the maps loading to prevent refresh cycles
    setTimeout(() => {
      try {
        console.log("AdminDashboard - forcing maps to load");
        forceLoaded();
        setMapsInitialized(true);
      } catch (err) {
        console.log("Error forcing maps to load:", err);
      }
    }, 500);
    
    // Only redirect if needed (debounced to prevent navigation loops)
    if (window.location.pathname === "/admin-dashboard") {
      setTimeout(() => {
        navigate("/admin-dashboard/overview");
      }, 100);
    }
  }, [navigate, forceLoaded]);
  
  return (
    <AdminErrorBoundary>
      <AdminLayout>
        <Routes>
          <Route path="overview" element={<DashboardOverview />} />
          <Route path="bookings" element={<BookingsManagement />} />
          <Route path="analytics" element={<Analytics />} />
          <Route path="fleet" element={<FleetManagement />} />
          <Route path="settings" element={<Settings />} />
          <Route path="users" element={<UserManagement />} />
          <Route index element={<Navigate to="overview" replace />} />
        </Routes>
      </AdminLayout>
    </AdminErrorBoundary>
  );
};

export default AdminDashboard;
   